import { CSSResult, html, LitElement, nothing, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import '../swiper/swiper.js';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import {
  Article,
  Image,
  Messages,
  OpenSizeLayerContainer,
  Price,
  Product,
  SizeDeterminedContainer,
  SizeSelectionContainer,
  ViewMode,
  WishlistCollection,
} from '../../../@types/wishlist.js';
import '../wishlist-remove-button/wishlist-remove-button.js';
import '../add-to-cart-button/add-to-cart-button.js';
import '../price/price.js';
import '../notify-me-button/notify-me-button.js';
import '../wishlist-button/wishlist-button.js';
import '../vsp-button/vsp-button.js';
import styles from './wishlist-product.scss?inline';
import grids from '../../../styles/grids.scss?inline';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import {
  productListView,
  trackSnackbarUndoWishlistItemDelete,
  trackWishlistClick,
} from '../../../scripts/service/cube-tracking-layer.js';
import { sizeLayerContext, SizeLayerState } from '../../../scripts/context/sizeLayerContext.js';
import { wishlistService } from '../../../scripts/service/wishlist-service.js';
import { buildPdsUrl } from '../../../scripts/service/build-pds-url.js';
import { productsContext } from '../../../scripts/context/wishlistContext.js';
import { cartContext } from '../../../scripts/context/cartContext.js';
import { ProductIdentifier } from '../../../scripts/service/product-identifier.js';
import { CartState, ProductsState } from '../../../scripts/context/wishlist-state.js';
import { productMenuContext } from '../../../scripts/context/menuContext.js';
import { ProductMenuState } from '../../../scripts/context/menu-state.js';
import { vtoContext } from '../../../scripts/context/vtoContext.js';
import { VtoService } from '../../../scripts/service/vto-service.js';
import type { VtoStatus } from '../../../scripts/service/vto-types.js';
import { mediaQuery } from '../../../scripts/service/media-query.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import saleIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-12_sale.svg?element';
import chevronRightIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-12_chevron-right.svg?element';
import basketCeck from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_basket-check.svg?element';
import boxCeck from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_box-check.svg?element';
import boxArrowLeft from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_box-arrow-left.svg?element';
import { addPresentationToIcon, addTitleToIcon } from '../icon/icon-accessibility.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { pushToSnackbar } from '../../../scripts/service/snackbar-service.js';
import { ADD_TO_WISHLIST_EVENT } from '../../../scripts/service/config.js';
import { getSinglePictureUrl } from '../../../scripts/service/image-formatter.js';

function firstOrNull<T>(items: T[]): T | null {
  if (items.length === 0) {
    return null;
  }
  return items[0];
}

@customElement('ents-wishlist-product')
export class WishlistProduct extends LitElement {
  @consume({ context: i18nContext })
  protected messages!: Messages;

  @consume({ context: productsContext, subscribe: true })
  private productsState!: ProductsState;

  @consume({ context: productMenuContext })
  private productMenuState!: ProductMenuState;

  @consume({ context: sizeLayerContext })
  private sizeLayerState!: SizeLayerState;

  @consume({ context: cartContext, subscribe: true })
  @state()
  private cartState!: CartState;

  @property({ type: Object, attribute: 'collection' })
  private collection?: WishlistCollection;

  @property({ type: Object, attribute: 'product' })
  private product!: Product;

  @property({ type: String, attribute: 'selected-article-id' })
  private selectedArticleId?: string;

  @property({ type: Number, attribute: 'list-count' })
  private totalListItemsCount!: number;

  @property({ type: Object, attribute: false })
  private globalVtoStatus: VtoStatus = { kind: 'ok' };

  @property({ type: Number, attribute: false })
  private vtoProfileVersion = 0;

  loggedIn: boolean = false;

  viewportObserver: IntersectionObserver | null = null;

  trackingObserver: IntersectionObserver | null = null;

  promotionObserver: MutationObserver | null = null;

  @property({ type: String, attribute: 'current-view' })
  private currentView: ViewMode = 'product';

  @property({ type: String, attribute: 'sharing-context' })
  private sharingContext: 'curated_collection' | 'collection' | 'not_shared' = 'not_shared';

  // === abtest-ents-t7700-favoriten-artikelkachel-redesign-v2 Product Tile Redesign v2 START ===
  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: vtoContext })
  private vtoService?: VtoService;

  @state()
  private vtoTileState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';

  @state()
  private vtoImage: Image | null = null;

  @state()
  private sizeLinkSize: 'extra-small' | 'small' = mediaQuery.widths.S_and_up ? 'small' : 'extra-small';

  @state()
  private redesignButtonSize: 'small' | 'medium' = this.calcRedesignButtonSize();

  @state()
  private triggerAddToCart: boolean = false;

  @state()
  private isInViewport: boolean = false;

  @state()
  private triggerNotifyMe: boolean = false;

  @state()
  private selectedArticle: Article | null = null;

  filteredListItemsCount?: number;

  position: number = 0;

  private forcedFullRendering: boolean = false;

  private viewportDebounceTimer?: number;

  private trackingDebounceTimer?: number;

  private hasTrackedView = false;

  private productIdentifier!: ProductIdentifier;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles), unsafeCSS(grids)];
  }

  private handleSizeBreakpointChange = () => {
    this.sizeLinkSize = mediaQuery.widths.S_and_up ? 'small' : 'extra-small';
  };

  private calcRedesignButtonSize(): 'small' | 'medium' {
    const { S_and_up, M_and_up, L_and_up, XL_and_up, XXL_and_up, fullHD_and_up } = mediaQuery.widths;
    const isMedium = (S_and_up && !M_and_up) || (L_and_up && !XL_and_up) || (XXL_and_up && !fullHD_and_up);
    return isMedium ? 'medium' : 'small';
  }

  private handleButtonSizeBreakpointChange = () => {
    this.redesignButtonSize = this.calcRedesignButtonSize();
  };

  private handleSizeChanges = () => {
    this.handleSizeBreakpointChange();
    this.handleButtonSizeBreakpointChange();
  };

  connectedCallback() {
    super.connectedCallback();
    this.productIdentifier = ProductIdentifier.createByProduct(this.product);
    if (this.selectedArticleId === undefined && this.product.articleId) {
      this.selectedArticleId = this.product.articleId;
      this.setSelectedArticle();
    }

    mediaQuery.on('change:width', this.handleSizeChanges);

    this.addEventListener('ents:size-layer:open', this.openSizeLayer);

    this.viewportObserver?.observe(this);
    if (!this.hasTrackedView) {
      this.trackingObserver?.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQuery.off('change:width', this.handleSizeChanges);
    this.removeEventListener('ents:size-layer:open', this.openSizeLayer);
    this.viewportObserver?.unobserve(this);
    this.trackingObserver?.unobserve(this);
    clearTimeout(this.viewportDebounceTimer);
    clearTimeout(this.trackingDebounceTimer);
  }

  protected firstUpdated(changedProperties: PropertyValues): void {
    super.firstUpdated(changedProperties);

    if (this.currentView === 'vto') {
      void this.maybeFetchVtoImage();
    }

    if (!this.product.price) {
      return;
    }

    // product includes a <camp-aktionsflag> element, will need a CAMP API call to render the flag
    this.dispatchEvent(new Event('aktionsflags:schedule-load'));

    if (!this.product.articles.some((a) => a.isInPromotion)) {
      // in case we do not know if the product is in a promotion (no articles have `isInPromotion` set to true), we need
      // an observer to set the `isAnyArticleInPromotion` flag on the product once CAMP has performed the API call and
      // updated the attributes of the <camp-aktionsflag> element
      this.registerPromotionObserver();
    }
  }

  private async maybeFetchVtoImage(): Promise<void> {
    if (this.sharingContext !== 'curated_collection') return;
    if (!this.wishlistAppConfig.vtoEnabled || !this.vtoService?.isAvailable()) return;
    if (!this.product.colorId) return;
    this.vtoTileState = 'loading';
    try {
      const result = await this.vtoService.getVto(this.product.syan, this.product.colorId);
      // The user may have toggled away from VTO while we were awaiting; don't overwrite their state.
      if (this.currentView !== 'vto') return;
      if (result) {
        this.vtoImage = { url: result.url, isModelView: false, aiGenerated: this.wishlistAppConfig.aiMarkingEnabled };
        this.vtoTileState = 'loaded';
      } else {
        this.vtoTileState = 'error';
      }
    } catch (e) {
      console.error('[vto]', 'unexpected error fetching VTO image', e);
      if (this.currentView === 'vto') this.vtoTileState = 'error';
    }
  }

  private handleImageLoadError = (e: CustomEvent<{ url: string }>) => {
    if (e.detail.url === this.vtoImage?.url) {
      console.error('[vto]', 'failed to load VTO image');
      this.vtoImage = null;
      this.vtoTileState = 'error';
    }
  };

  protected willUpdate(changedProperties: PropertyValues): void {
    super.willUpdate(changedProperties);
    // Only recalculate if inputs changed
    if (changedProperties.has('product') || changedProperties.has('selectedArticleId')) {
      this.setSelectedArticle();
    }
    if (changedProperties.has('totalListItemsCount')) {
      this.classList.toggle('large-list', this.totalListItemsCount >= 100);
    }
    if (changedProperties.has('currentView') && this.currentView === 'vto' && this.vtoTileState === 'idle') {
      void this.maybeFetchVtoImage();
    }
    // Undefined on the very first update (no prior value to compare against) — only react to a real bump.
    if (changedProperties.has('vtoProfileVersion') && changedProperties.get('vtoProfileVersion') !== undefined) {
      // The profile photo changed: the previously fetched/cached VTO image is stale regardless of tile state.
      this.vtoImage = null;
      this.vtoTileState = 'idle';
      if (this.currentView === 'vto') void this.maybeFetchVtoImage();
    }
    if (changedProperties.has('globalVtoStatus')) {
      // Only retry on recovery FROM a non-ok global state. A tile-specific permanent failure
      // (e.g. ARTICLE_NOT_FOUND) is cached as null in the service and would otherwise be re-attempted
      // for every cooldown recovery happening elsewhere on the page.
      const oldStatus = changedProperties.get('globalVtoStatus') as VtoStatus | undefined;
      if (
        oldStatus &&
        oldStatus.kind !== 'ok' &&
        this.globalVtoStatus.kind === 'ok' &&
        this.vtoTileState === 'error' &&
        this.currentView === 'vto'
      ) {
        this.vtoTileState = 'idle';
        void this.maybeFetchVtoImage();
      }
    }
  }

  private isShared() {
    return this.sharingContext === 'collection' || this.sharingContext === 'curated_collection';
  }

  public animateRemoval() {
    if (!this.isShared()) {
      this.style.transition = 'opacity 0.4s linear';
      if (mediaQuery.prefersReducedMotion()) {
        this.style.transition = 'none';
      }
      this.style.opacity = '0';
      const handleTransitionEnd = (event: TransitionEvent) => {
        if (event.propertyName === 'opacity') {
          this.style.display = 'none';
          this.removeEventListener('transitionend', handleTransitionEnd);
        }
      };
      this.addEventListener('transitionend', handleTransitionEnd);
    }
  }

  public animateAddition() {
    if (!this.isShared()) {
      this.removeAttribute('style');
    }
  }

  public resetTriggers() {
    this.triggerNotifyMe = false;
    this.triggerAddToCart = false;
  }

  public handleAddToCart(detail: SizeDeterminedContainer) {
    if (detail.syan !== this.product.syan) {
      return;
    }
    // "itemId" is the official DIST term, replacing articleId (ENTS-10800)
    const articleId = detail.itemId ?? detail.articleId;
    if (!this.product.articles.some((a) => a.id === articleId)) {
      return;
    }

    this.selectedArticleId = articleId;
    this.setSelectedArticle();

    if (this.selectedArticle !== null) {
      this.triggerAddToCart = !this.selectedArticle.isOutOfStock;
      this.triggerNotifyMe = this.selectedArticle.isOutOfStock;
    }
  }

  public async handleSizeSelection(detail: SizeSelectionContainer) {
    await this.applySelectedArticle(detail.articleId, detail.triggerCta);
  }

  private setSelectedArticle() {
    if (!this.product.hasSizeOrQuantitySelection) {
      this.selectedArticle = firstOrNull(this.product.articles);
    } else if (this.selectedArticleId === undefined) {
      this.selectedArticle = null;
    } else {
      this.selectedArticle = this.product.articles.find((a) => a.id === this.selectedArticleId) || null;
    }
  }

  private registerPromotionObserver() {
    const flagElement = this.shadowRoot?.querySelector('camp-aktionsflag');
    if (!flagElement || !this.promotionObserver) {
      return;
    }

    this.promotionObserver.observe(flagElement, {
      attributes: true,
      attributeFilter: ['contains-flag', 'artikel-id'],
    });
  }

  public handlePromotionDetected(articleId: string | null) {
    if (articleId) {
      this.product.articles = this.product.articles.map((a) =>
        a.id === articleId ? { ...a, isInPromotion: true } : a,
      );
    } else {
      this.product.isAnyArticleInPromotion = true;
    }
    this.requestUpdate();
  }

  private async applySelectedArticle(articleId: string, triggerCta: boolean) {
    this.selectedArticleId = articleId;
    this.setSelectedArticle();
    if (this.selectedArticle === null) {
      return;
    }

    this.triggerNotifyMe = triggerCta && this.selectedArticle.isOutOfStock;
    this.triggerAddToCart = triggerCta && !this.selectedArticle.isOutOfStock;

    if (!this.isShared()) {
      await wishlistService.changeItemOnWishlist(this.product.syan, this.product.colorId, this.selectedArticle.id);
    }
    this.productsState.updateSelectedArticle(this.productIdentifier, this.selectedArticle.id);
  }

  private openSizeLayer = (e: CustomEvent<OpenSizeLayerContainer>) => {
    this.sizeLayerState.update({
      product: this.product,
      trackingContext: e.detail.tracking.context,
      triggerA2C: e.detail.addToCartAfterSizeSelection,
      open: true,
      selectedArticle: this.selectedArticle,
      backButtonCallback: undefined,
    });
  };

  private getImages(): Image[] {
    const baseImages = this.getAttrFromSelected(
      (a: Article) => a.imageUrls,
      (p: Product) => p.defaultImageUrls,
    );
    if (this.currentView === 'vto' && this.vtoTileState === 'loaded' && this.vtoImage) {
      return baseImages.length === 0 ? [this.vtoImage] : [baseImages[0], this.vtoImage, ...baseImages.slice(1)];
    }
    return baseImages;
  }

  private selectInitialImageIndex(images: Image[]): number {
    if (this.currentView === 'model' || (this.currentView === 'vto' && !this.vtoImage)) {
      const modelViewIndex = images.findIndex((image) => image.isModelView);
      return modelViewIndex !== -1 ? modelViewIndex : 0;
    }
    if (this.currentView === 'vto' && this.vtoImage) {
      const vtoIndex = images.findIndex((image) => image.url === this.vtoImage!.url);
      if (vtoIndex !== -1) return vtoIndex;
    }
    return 0;
  }

  private getPdsUrl() {
    return buildPdsUrl(this.product.pdsUrlBasePath, this.selectedArticleId, this.product.colorId);
  }

  private getTrackingUrl() {
    // for tracking it is ok to hardcode the PROD host (cleared with PO)
    return (
      'https://www.breuninger.com' +
      buildPdsUrl(this.product.pdsUrlBasePath, this.selectedArticleId, this.product.colorId)
    );
  }

  private getPrice(): Price | undefined {
    return this.getAttrFromSelected(
      (a: Article) => a.price,
      (p: Product) => p.price,
    );
  }

  private getMixAndMatch(): string | undefined {
    return this.getAttrFromSelected(
      (a: Article) => a.mixAndMatchMessage,
      (p: Product) => p.mixAndMatchMessage,
    );
  }

  private getLimited(): string | undefined {
    return this.getAttrFromSelected(
      (a: Article) => a.limitedMessage,
      (p: Product) => p.limitedMessage,
    );
  }

  private getExclusive(): string | undefined {
    return this.getAttrFromSelected(
      (a: Article) => a.exclusiveMessage,
      (p: Product) => p.exclusiveMessage,
    );
  }

  private getCertifiedMessage(): string | undefined {
    return this.getAttrFromSelected(
      (a: Article) => a.certifiedMessage,
      (p: Product) => p.certifiedMessage,
    );
  }

  private showNotificationButton(): boolean {
    return (
      this.product.price !== undefined &&
      this.getAttrFromSelected(
        (a: Article) => a.isOutOfStock,
        (p: Product) => p.isOutOfStock,
      )
    );
  }

  private getSizeOrQuantity() {
    return this.getAttrFromSelected(
      (a: Article) => a.sizeOrQuantity,
      () => '',
    );
  }

  private getAttrFromSelected<T>(fromSelected: (article: Article) => T, defaultAttr: (product: Product) => T): T {
    return this.selectedArticle !== null ? fromSelected(this.selectedArticle) : defaultAttr(this.product);
  }

  private renderNotificationButton() {
    return html`
      <ents-notify-me-button
        identifier="${this.productIdentifier.id}"
        salesinformation-id="${this.selectedArticle ? this.selectedArticle.salesInformationId : ''}"
        ?trigger="${this.triggerNotifyMe}"
        .additionalInfoForAriaLabel="${this.getProductInfo()}"
        size="${this.wishlistAppConfig.productTileRedesign ? this.redesignButtonSize : 'medium'}"
        data-test-ents-notify-me-button="${this.product.syan}"
      >
      </ents-notify-me-button>
    `;
  }

  private getProductInfo(): string {
    const sizeInfo = this.product.hasSizeOrQuantitySelection
      ? (this.getInputWithLabelIfExists('ents_wishlist_product_size', this.getSizeOrQuantity()) ??
        this.messages['ents_wishlist_product_selectSize'])
      : undefined;
    return this.filterUndefinedAndJoin([
      this.getSmallProductInfo(),
      this.getInputWithLabelIfExists('ents_wishlist_product_color', this.product.colorName),
      this.getOutOfStockMessage(),
      this.getInputWithLabelIfExists(
        'ents_wishlist_product_price_currentPrice_label',
        this.getPrice()?.currentPriceMessage,
      ),
      sizeInfo,
    ]);
  }

  private getSmallProductInfo(): string {
    return this.filterUndefinedAndJoin([this.product.brandName, this.product.name, this.product.subName]);
  }

  private filterUndefinedAndJoin(values: (string | undefined)[]): string {
    return values.filter((v): v is string => v !== undefined).join(' ');
  }

  private getInputWithLabelIfExists(labelKey: string, value?: string) {
    return value ? `${this.messages[labelKey]} ${value}` : undefined;
  }

  private renderAddToCartButton() {
    return html`
      <ents-add-to-cart-button
        identifier="${this.productIdentifier.id}"
        .additionalInfoForAriaLabel="${this.getProductInfo()}"
        article-id="${this.selectedArticle ? this.selectedArticle.id : ''}"
        sales-information-id="${this.selectedArticle ? this.selectedArticle.salesInformationId : ''}"
        ?trigger="${this.triggerAddToCart}"
        size="${this.wishlistAppConfig.productTileRedesign ? this.redesignButtonSize : 'medium'}"
        data-test-ents-add-to-cart-button="${this.product.syan}"
      >
      </ents-add-to-cart-button>
    `;
  }

  private renderVspButton() {
    if (!this.product.isVspButtonEnabled || this.wishlistAppConfig.productTileRedesign) {
      return nothing;
    }
    return html`
      <ents-vsp-button
        .name="${this.getSmallProductInfo()}"
        syan="${this.product.syan}"
        color-id="${this.product.colorId}"
        data-test-ents-vsp-button="${this.product.syan}"
      ></ents-vsp-button>
    `;
  }

  private renderSwiper(available: boolean) {
    const images = this.getImages();
    const initialImageIndex = this.selectInitialImageIndex(images);

    return html`
      <ents-swiper
        class="${ifDefined(this.wishlistAppConfig.productTileRedesign ? 'ents-swiper--redesign' : undefined)}"
        position="${this.position}"
        initial-image-index="${initialImageIndex}"
        .images="${images}"
        ?is-faded="${!available}"
        ?in-viewport="${this.isInViewport}"
        .product="${this.product}"
        alt-text="${this.product.brandName} ${this.product.name}"
        @ents:image-load-error="${this.handleImageLoadError}"
      >
      </ents-swiper>
      ${this.renderVtoOverlay()}
    `;
  }

  private renderVtoOverlay() {
    if (this.currentView !== 'vto') return nothing;

    if (this.vtoTileState === 'loading') {
      return html`
        <div
          class="ents-wishlist-vto-overlay ents-wishlist-vto-overlay--loading"
          data-test-ents-vto-loading
          role="status"
          aria-live="polite"
        >
          <span class="ents-wishlist-vto-overlay__spinner"></span>
          <span class="ents-copy-small">${this.messages['ents_wishlist_vto_loading']}</span>
        </div>
      `;
    }

    return nothing;
  }

  private renderFlags() {
    return html`
      <div class="ents-wishlist-flags" data-test-ents-wishlist-flags>
        <camp-aktionsflag
          class="ents-wishlist-flags--item ents-wishlist-flags--item__campaign-flag"
          data-test-ents-wishlist-camp-ssi
          syan="${this.product.syan}"
          farb-id="${this.product.colorId}"
          artikel-id="${this.selectedArticleId ? this.selectedArticleId : this.product.articleId}"
        >
        </camp-aktionsflag>
        ${this.renderSaleBadge()}
        ${
          this.getExclusive()
            ? html`
                <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
                  ${this.getExclusive()}
                </div>
              `
            : nothing
        }
        ${
          this.getLimited()
            ? html`
                <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
                  ${this.getLimited()}
                </div>
              `
            : nothing
        }
        ${
          this.product.newMessage && !this.getPrice()?.isReduced
            ? html`
                <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
                  ${this.product.newMessage}
                </div>
              `
            : nothing
        }
        ${
          this.getCertifiedMessage()
            ? html`
                <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
                  ${this.getCertifiedMessage()}
                </div>
              `
            : nothing
        }
        ${
          this.getMixAndMatch()
            ? html`
                <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
                  ${unsafeHTML(this.getMixAndMatch())}
                </div>
              `
            : nothing
        }
        ${
          this.product.easyironMessage
            ? html`
                <div class="bdk-badge-article ents-wishlist-flags--item" variant="concrete" size="small">
                  ${this.product.easyironMessage}
                </div>
              `
            : nothing
        }
      </div>
    `;
  }

  renderSaleBadge() {
    const saleFlagMessage = this.getPrice()?.saleFlagMessage;
    if (saleFlagMessage == null) {
      return nothing;
    }
    return html`
      <div
        class="bdk-badge-article ents-wishlist-flags--item ents-wishlist-flags--item__sale"
        variant="sale"
        size="small"
      >
        ${saleFlagMessage ? html`<span>${saleFlagMessage}</span>` : nothing}
        ${addTitleToIcon(saleIcon(), this.messages['ents_wishlist_filter_option_onSale'])}
      </div>
    `;
  }

  private renderNames() {
    return html`
      <div class="ents-wishlist-brand-name">${this.product.brandName}</div>
      <div class="ents-wishlist-product-names">
        <div class="ents-wishlist-product-name">${this.product.name}</div>
      </div>
    `;
  }

  private renderUnavailableProduct() {
    if (this.wishlistAppConfig.productTileRedesign) {
      return this.renderUnavailableProductRedesign();
    }
    return html`
      <div class="ents-wishlist-product-grid" @click="${this.productListClickEvent}">
        <div class="ents-wishlist-swiper-container">${this.renderSwiper(false)}</div>
        <div class="ents-wishlist-product-infos">
          ${this.useMinimalRender ? nothing : this.renderNames()}
          ${this.useMinimalRender ? nothing : this.renderColor()}
          ${this.useMinimalRender ? nothing : this.renderUnavailableMessage()}
        </div>
      </div>
      ${this.useMinimalRender ? nothing : this.renderRemoveOrHeartButton()}
      <div class="ents-wishlist-product-cta--container">
        ${this.useMinimalRender ? nothing : this.renderActionButton()}
        ${this.useMinimalRender ? nothing : this.renderVspButton()}
      </div>
    `;
  }

  private renderUnavailableProductRedesign() {
    return html`
      <div class="ents-wishlist-product-grid" @click="${this.productListClickEvent}">
        <div class="ents-wishlist-image-wrapper">
          <div class="ents-wishlist-swiper-container">${this.renderSwiper(false)}</div>
          ${this.useMinimalRender ? nothing : this.renderRemoveOrHeartButton()}
        </div>
        <div class="ents-wishlist-info-column">
          <div class="ents-wishlist-product-infos">
            ${this.useMinimalRender ? nothing : this.renderNames()}
            ${this.useMinimalRender ? nothing : this.renderColor()}
            ${this.useMinimalRender ? nothing : this.renderUnavailableMessage()}
          </div>
          <div class="ents-wishlist-product-cta--container">
            ${this.useMinimalRender ? nothing : this.renderPrimaryButton()}
          </div>
        </div>
      </div>
    `;
  }

  private renderUnavailableMessage() {
    return html` <div
      class="ents-copy-extra-small-bold ents-s:copy-small-bold ents-wishlist-unavailable-info"
      data-test-ents-wishlist-unavailable-info
    >
      ${this.product.unavailableMessage}
    </div>`;
  }

  private renderAvailableProduct() {
    if (this.wishlistAppConfig.productTileRedesign) {
      return this.renderAvailableProductRedesign();
    }
    return html`
      <div class="ents-wishlist-product-grid">
        <a
          target="_self"
          href="${this.getPdsUrl()}"
          @click="${this.productListClickEvent}"
          aria-label="${this.getSmallProductInfo()}"
          data-test-ents-wishlist-pds-link
        >
          <div class="ents-wishlist-pds-container" data-test-ents-wishlist-pds-container>
            ${this.renderSwiper(true)} ${this.renderInCartHint()} ${this.renderPurchasedHint()}
          </div>
        </a>
        <a
          target="_self"
          href="${this.getPdsUrl()}"
          @click="${this.productListClickEvent}"
          aria-label="${this.getSmallProductInfo()}"
          data-test-ents-wishlist-pds-link
        >
          <div class="ents-wishlist-product-infos" data-test-ents-wishlist-product-info>
            ${this.renderFlags()} ${this.useMinimalRender ? nothing : this.renderNames()}
            ${this.useMinimalRender ? nothing : this.renderColor()}
            ${this.useMinimalRender ? nothing : this.renderSize()}
            ${this.useMinimalRender ? nothing : this.renderShortageInfo()}
            ${this.useMinimalRender ? nothing : html` <ents-price .price="${this.getPrice()}"></ents-price>`}
          </div>
        </a>
      </div>
      ${this.useMinimalRender ? nothing : this.renderRemoveOrHeartButton()}
      <div class="ents-wishlist-product-cta--container">
        ${this.useMinimalRender ? nothing : this.renderActionButton()}
        ${this.useMinimalRender ? nothing : this.renderPrimaryButton()}
      </div>
    `;
  }

  private renderAvailableProductRedesign() {
    return html`
      <div class="ents-wishlist-product-grid">
        <div class="ents-wishlist-image-wrapper">
          <a
            target="_self"
            href="${this.getPdsUrl()}"
            @click="${this.productListClickEvent}"
            aria-label="${this.getSmallProductInfo()}"
            data-test-ents-wishlist-pds-link
          >
            <div class="ents-wishlist-pds-container" data-test-ents-wishlist-pds-container>
              ${this.renderSwiper(true)} ${this.renderInCartHint()} ${this.renderPurchasedHint()}
            </div>
          </a>
          ${this.useMinimalRender ? nothing : this.renderRemoveOrHeartButton()}
        </div>
        <div class="ents-wishlist-info-column">
          <a
            target="_self"
            href="${this.getPdsUrl()}"
            @click="${this.productListClickEvent}"
            aria-label="${this.getSmallProductInfo()}"
            data-test-ents-wishlist-pds-link
          >
            <div class="ents-wishlist-product-infos" data-test-ents-wishlist-product-info>
              ${this.renderFlags()} ${this.useMinimalRender ? nothing : this.renderNames()}
              ${this.useMinimalRender ? nothing : this.renderColor()}
              ${this.useMinimalRender ? nothing : this.renderSize()}
              ${this.useMinimalRender ? nothing : this.renderShortageInfo()}
            </div>
            ${
              this.useMinimalRender
                ? nothing
                : html` <div class="ents-wishlist-price--redesign">
                    <ents-price .price="${this.getPrice()}"></ents-price>
                  </div>`
            }
          </a>
          <div class="ents-wishlist-product-cta--container">
            ${this.useMinimalRender ? nothing : this.renderPrimaryButton()}
          </div>
        </div>
      </div>
    `;
  }

  private renderColor() {
    return this.product.colorName && this.product.isBeauty
      ? html`
          <div class="ents-copy-extra-small ents-s:copy-small ents-wishlist-color">
            ${this.messages['ents_wishlist_product_color']} ${this.product.colorName}
          </div>
        `
      : nothing;
  }

  private renderSize() {
    if (this.wishlistAppConfig.productTileRedesign) {
      return this.renderSizeLink();
    }
    const selectedSizeOrQuantity = this.getSizeOrQuantity();
    return selectedSizeOrQuantity
      ? html`
          <div class="ents-copy-extra-small ents-s:copy-small ents-wishlist-size" aria-live="polite">
            <span>${this.messages['ents_wishlist_product_size']}</span>
            <span data-test-ents-selected-size>${selectedSizeOrQuantity}</span>
          </div>
        `
      : nothing;
  }

  private renderSizeLink() {
    if (!this.product.hasSizeOrQuantitySelection) {
      return nothing;
    }
    const selectedSizeOrQuantity = this.getSizeOrQuantity();
    const label = selectedSizeOrQuantity
      ? html`<span
          >${this.messages['ents_wishlist_product_size']}
          <span data-test-ents-selected-size>${selectedSizeOrQuantity}</span></span
        >`
      : html`<span>${this.messages['ents_wishlist_product_selectSize']}</span>`;
    return html`
      <div class="ents-wishlist-size" aria-live="polite">
        <button
          class="bdk-link-primary"
          size="${this.sizeLinkSize}"
          type="button"
          @click="${this.handleSizeLinkClick}"
          data-test-ents-size-link
        >
          ${label}
        </button>
      </div>
    `;
  }

  private handleSizeLinkClick = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    this.sizeLayerState.update({
      product: this.product,
      trackingContext: 'wishlist',
      triggerA2C: false,
      open: true,
      selectedArticle: this.selectedArticle,
      backButtonCallback: undefined,
    });
  };

  private getOutOfStockMessage() {
    if (!this.product.isOutOfStock && !this.selectedArticle?.isOutOfStock) {
      return undefined;
    }

    if (this.wishlistAppConfig.productTileRedesign) {
      return this.messages['ents_wishlist_product_soldOut_redesign'];
    }

    const isProductWithSelectedSize = this.product.isNotOneSize && this.selectedArticle?.sizeOrQuantity;
    return isProductWithSelectedSize
      ? this.messages['ents_wishlist_product_sizeSoldOut']
      : this.messages['ents_wishlist_product_soldOut'];
  }

  private renderShortageInfo() {
    if (this.selectedArticle?.lowStockMessage) {
      return html` <div
        class="ents-copy-extra-small-bold ents-s:copy-small-bold ents-wishlist-shortage-info"
        data-test-ents-wishlist-shortage-info
      >
        ${this.selectedArticle.longLowStockMessage}
      </div>`;
    }

    const outOfStockMessage = this.getOutOfStockMessage();
    if (outOfStockMessage) {
      return html` <div
        class="ents-copy-extra-small-bold ents-s:copy-small-bold ents-wishlist-out-of-stock-info"
        data-test-ents-wishlist-out-of-stock-info
      >
        ${outOfStockMessage}
      </div>`;
    }
    return nothing;
  }

  private renderRemoveOrHeartButton() {
    if (this.isShared()) {
      return this.renderHeartButton('ents-wishlist-product__wishlist-button');
    }

    if (this.wishlistAppConfig.productTileRedesign) {
      return this.renderRedesignHeartOverlay();
    }

    return this.renderRemoveButton();
  }

  private renderRedesignHeartOverlay() {
    const onOwnWishlist = this.productsState.find(this.productIdentifier) !== undefined;
    return html`
      <div
        class="ents-wishlist-product__heart-overlay"
        data-test-ents-heart-overlay
        @click="${{ handleEvent: this.handleHeartOverlayClick, capture: true }}"
      >
        <ents-wishlist-button
          initial-state="${onOwnWishlist ? [`["${this.productIdentifier.id}"]`] : ['']}"
          syan="${this.product.syan}"
          color-id="${this.product.colorId}"
          size="large"
          position="${this.position}"
          placement=""
        >
        </ents-wishlist-button>
      </div>
    `;
  }

  private handleHeartOverlayClick = () => {
    const onOwnWishlist = this.productsState.find(this.productIdentifier) !== undefined;
    if (!onOwnWishlist) return;

    const isOnAnyCollection = (this.product.collectionReferences?.length ?? 0) > 0;
    const images = this.getImages();
    const rawUrl = images[this.selectInitialImageIndex(images)]?.url;
    const imageUrl = rawUrl ? getSinglePictureUrl(rawUrl, 'webp') : '';
    const articleDeletedText = isOnAnyCollection
      ? this.messages['ents_wishlist_delete_all_title']!
      : this.messages['ents_wishlist_delete_title']!;

    pushToSnackbar(articleDeletedText, imageUrl, this.messages['ents_wishlist_delete_buttontext'], () => {
      trackSnackbarUndoWishlistItemDelete(this.product);
      document.dispatchEvent(
        new CustomEvent(ADD_TO_WISHLIST_EVENT, {
          detail: {
            identifier: this.productIdentifier.id,
            syan: this.product.syan,
            colorId: this.product.colorId,
            articleId: this.selectedArticle?.id,
            added: this.product.added,
            purchased: this.product.purchaseDetails?.purchased,
            collectionReferences: this.product.collectionReferences,
            tracking: {
              placement: 'wishlist',
              context: 'wishlist_action_menu',
              added: this.product.added,
            },
          },
        }),
      );
    });
  };

  private renderHeartButton(wrapperClass: string) {
    const onOwnWishlist = this.productsState.find(this.productIdentifier) !== undefined;
    const placement = this.sharingContext !== 'not_shared' ? this.sharingContext : '';
    return html`
      <div class="${wrapperClass}">
        <ents-wishlist-button
          initial-state="${onOwnWishlist ? [`["${this.productIdentifier.id}"]`] : ['']}"
          syan="${this.product.syan}"
          color-id="${this.product.colorId}"
          size="large"
          position="${this.position}"
          placement="${placement}"
        >
        </ents-wishlist-button>
      </div>
    `;
  }

  private renderRemoveButton() {
    return html`
      <div class="ents-wishlist-product__remove-button">
        <ents-wishlist-remove-button
          .collection="${this.collection}"
          .product="${this.product}"
          data-test-ents-wishlist-remove-button="${this.product.syan}"
        >
          ${this.messages['ents_wishlist_product_cta_remove']}
        </ents-wishlist-remove-button>
      </div>
    `;
  }

  private renderInCartHint() {
    return html`
      ${
        this.cartState.isInCart(this.product)
          ? html`
              <div
                class="bdk-badge-article ents-wishlist-product__in-cart-hint"
                size="medium"
                data-test-ents-wishlist-in-cart-indicator
              >
                <div>
                  ${addTitleToIcon(basketCeck(), this.messages['ents_wishlist_product_in_cart'])}
                  <span data-test-ents-wishlist-in-cart-indicator-content>
                    ${this.messages['ents_wishlist_product_in_cart']}
                  </span>
                </div>
              </div>
            `
          : nothing
      }
    `;
  }

  private renderPurchasedHint() {
    if (this.cartState.isInCart(this.product) || !this.product.purchaseDetails?.orderUrl) return nothing;

    const isReturned = !!this.product.purchaseDetails?.returned;
    const label = isReturned ? this.messages['ents_wishlist_last_return'] : this.messages['ents_wishlist_last_order'];
    const icon = isReturned ? boxArrowLeft() : boxCeck();

    return html`
      <button
        class="bdk-badge-article ents-wishlist-product__purchased-hint"
        role="button"
        aria-label="${label}"
        @click="${this.openOrderDrawer}"
        size="medium"
        data-test-ents-wishlist-purchased-indicator
      >
        <div>
          ${addTitleToIcon(icon, label)}
          <span data-test-ents-wishlist-purchased-indicator-content>${label}</span>
        </div>
        ${addPresentationToIcon(chevronRightIcon())}
      </button>
    `;
  }

  private openOrderDrawer(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.updateProductBanner();
    const images = this.getImages();
    const initialImage = images[this.selectInitialImageIndex(images)];
    this.productMenuState.openMenu('purchased', this.product, {
      collection: this.collection ?? null,
      imageUrl: initialImage?.url,
      aiGenerated: initialImage?.aiGenerated === true,
      selectedArticle: this.selectedArticle,
    });
  }

  private renderActionButton() {
    if (this.wishlistAppConfig.productTileRedesign) {
      return nothing;
    }
    return html` <div class="ents-wishlist-action-button" data-test-ents-action-button-shadow>
      <button
        class="bdk-button-icon"
        size="medium"
        variant="outline"
        type="button"
        @click="${this.handleActionButtonClick}"
        data-test-ents-action-button
        aria-label="${this.messages['ents_wishlist_actionmenu_button_description']}"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" role="presentation">
          <circle cx="4.667" cy="12" r="2" fill="currentColor" />
          <circle cx="12.667" cy="12" r="2" fill="currentColor" />
          <circle cx="20.667" cy="12" r="2" fill="currentColor" />
        </svg>
      </button>
    </div>`;
  }

  private renderRedesignInlineControls() {
    const showNotification = this.showNotificationButton();
    const showVsp = this.shouldShowVsp();

    if (showVsp) {
      return this.renderVspButtonOutline();
    }
    if (showNotification) {
      return this.renderNotificationButton();
    }
    if (!this.product.price) {
      return nothing;
    }
    return this.renderAddToCartButton();
  }

  private renderVspButtonOutline() {
    return html`
      <ents-vsp-button
        variant="outline"
        size="${this.redesignButtonSize}"
        .label="${this.messages['ents_wishlist_product_cta_vsp_redesign'] ?? ''}"
        .name="${this.getSmallProductInfo()}"
        syan="${this.product.syan}"
        color-id="${this.product.colorId}"
        data-test-ents-vsp-button="${this.product.syan}"
      ></ents-vsp-button>
    `;
  }

  private shouldShowVsp(): boolean {
    return !this.product.price && this.product.isVspButtonEnabled;
  }

  private handleActionButtonClick() {
    this.updateProductBanner();
    const images = this.getImages();
    const initialImage = images[this.selectInitialImageIndex(images)];
    this.productMenuState.openMenu('default', this.product, {
      collection: this.collection ?? null,
      imageUrl: initialImage?.url,
      aiGenerated: initialImage?.aiGenerated === true,
      selectedArticle: this.selectedArticle,
    });
  }

  // === abtest-ents-t7700-favoriten-artikelkachel-redesign-v2 Product Tile Redesign v2 END ===

  private updateProductBanner() {
    const eventPayload = {
      syan: this.product.syan,
      farbId: this.product.colorId,
      artikelId: this.selectedArticle?.id,
    };
    document.dispatchEvent(new CustomEvent('bewe:ents:variantenwechsel', { detail: eventPayload })); // shared event used by other teams (e.g. BRTN, CAMP, KAUF)
    document.dispatchEvent(new CustomEvent('ents:wishlist:variant:changed', { detail: eventPayload })); // Translation
    const actionMenuButton = this.shadowRoot?.querySelector<HTMLButtonElement>('[data-test-ents-action-button]');
    actionMenuButton?.setAttribute('data-test-ents-product-banner-payload-emitted', JSON.stringify(eventPayload));
  }

  private renderPrimaryButton() {
    if (this.wishlistAppConfig.productTileRedesign) {
      return html`${this.renderRedesignInlineControls()}`;
    }
    if (this.showNotificationButton()) {
      return html`${this.renderNotificationButton()}`;
    }
    return html`${this.renderAddToCartButton()}`;
  }

  protected render(): TemplateResult {
    const articleClasses = this.wishlistAppConfig.productTileRedesign ? 'ents-wishlist-product--redesign' : '';
    return html`
      <article class="${articleClasses}">
        ${this.product.price ? this.renderAvailableProduct() : this.renderUnavailableProduct()}
      </article>
    `;
  }

  shouldUpdate(changedProperties: PropertyValues): boolean {
    if (this.hidden && !changedProperties.has('product')) {
      return false;
    }

    for (const [key, oldValue] of changedProperties.entries()) {
      if (key === 'productsState' && !this.isShared() && !changedProperties.has('sharingContext')) {
        continue;
      }
      if (key === 'cartState') {
        const oldCartState = oldValue as CartState | undefined;
        if (this.product && oldCartState?.isInCart(this.product) === this.cartState.isInCart(this.product)) {
          continue;
        }
      }
      return super.shouldUpdate(changedProperties);
    }
    return false;
  }

  public forceFullRendering(): void {
    this.forcedFullRendering = true;
    this.isInViewport = true;
  }

  public setInViewportDebounced(isInViewport: boolean): void {
    clearTimeout(this.viewportDebounceTimer);
    if (this.hidden || this.isInViewport === isInViewport) return;
    if (isInViewport) {
      this.isInViewport = true;
    } else {
      this.viewportDebounceTimer = window.setTimeout(() => {
        if (this.forcedFullRendering) return;
        this.isInViewport = false;
      }, 300);
    }
  }

  public trackViewDebounced(isInViewport: boolean): void {
    if (this.hasTrackedView || this.hidden) return;

    clearTimeout(this.trackingDebounceTimer);
    this.trackingDebounceTimer = window.setTimeout(() => {
      if (this.hasTrackedView || !isInViewport || this.hidden) return;
      this.trackingObserver?.unobserve(this);
      this.hasTrackedView = true;
      this.trackView();
    }, 300);
  }

  private trackView() {
    productListView(this.product, this.selectedArticle, this.position, this.loggedIn, this.filteredListItemsCount);
  }

  private productListClickEvent() {
    trackWishlistClick(
      this.product,
      this.selectedArticle,
      this.position,
      this.filteredListItemsCount ?? this.totalListItemsCount,
      this.loggedIn,
      this.getTrackingUrl(),
    );
  }

  private get useMinimalRender(): boolean {
    return this.totalListItemsCount >= 100 && !this.isInViewport;
  }
}
